import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import React from 'react';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { UnstyledButton } from 'src/design/atoms/UnstyledButton';
import { Table } from 'src/design/molecules/Table';
import { formatHoursCompact, logger } from 'src/utils';
import type {
  IDayHours,
  ITimesheetWeekHours,
} from 'src/app/timesheets/buildTimesheetWeekHours';
import {
  TIMESHEET_DAY_KEYS,
  TIMESHEET_DAY_LABELS,
  TIMESHEET_GRID_MIN_WIDTH,
  timesheetGridColumns,
  timesheetGridLabelColumn,
  timesheetGridTableStyle,
} from 'src/app/timesheets/timesheetGrid';

export interface ITimesheetWeekHoursPanelProps {
  model: ITimesheetWeekHours;
  isServiceJob: boolean;
  pending?: boolean;
  unavailable?: boolean;
}

export interface IExpandedGroups {
  other: boolean;
  week: boolean;
}

const PLACEHOLDER = '—';
const STORAGE_KEY = 'timesheet.weekHours.expanded';
const DEFAULT_EXPANDED: IExpandedGroups = { other: false, week: true };

const readExpanded = (): IExpandedGroups => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_EXPANDED;
    const parsed = JSON.parse(raw) as Partial<IExpandedGroups>;
    return {
      other:
        typeof parsed.other === 'boolean'
          ? parsed.other
          : DEFAULT_EXPANDED.other,
      week:
        typeof parsed.week === 'boolean' ? parsed.week : DEFAULT_EXPANDED.week,
    };
  } catch (err) {
    logger.debug('Failed to read timesheet week hours expand state', { err });
    return DEFAULT_EXPANDED;
  }
};

const writeExpanded = (state: IExpandedGroups) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    logger.debug('Failed to persist timesheet week hours expand state', {
      err,
    });
  }
};

export interface IValueCellsProps {
  days: IDayHours;
  total: number;
  placeholder?: boolean;
}

const ValueCells: React.FC<IValueCellsProps> = ({
  days,
  total,
  placeholder = false,
}) => {
  const cell = (value: number, style: React.CSSProperties, key: string) => (
    <Table.Td
      key={key}
      ta="center"
      style={style}
      c={placeholder ? 'dimmed' : undefined}
    >
      {placeholder ? PLACEHOLDER : formatHoursCompact(value)}
    </Table.Td>
  );

  return (
    <>
      {TIMESHEET_DAY_KEYS.map((day) =>
        cell(days[day], timesheetGridColumns.day, day),
      )}
      {cell(total, timesheetGridColumns.total, 'total')}
    </>
  );
};

export interface IGroupToggleProps {
  expanded: boolean;
  label: string;
  testId: string;
  onToggle: () => void;
}

const GroupToggle: React.FC<IGroupToggleProps> = ({
  expanded,
  label,
  testId,
  onToggle,
}) => (
  <UnstyledButton
    type="button"
    aria-expanded={expanded}
    aria-label={label}
    data-testid={testId}
    onClick={onToggle}
  >
    {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
  </UnstyledButton>
);

/** @registry Read-only panel under the timesheet entry grid showing this timesheet, the user's other jobs, and the week total by day, expandable per job and per pay code. */
export const TimesheetWeekHoursPanel: React.FC<
  ITimesheetWeekHoursPanelProps
> = ({ model, isServiceJob, pending = false, unavailable = false }) => {
  const [expanded, setExpanded] = React.useState<IExpandedGroups>(readExpanded);

  const toggle = (group: keyof IExpandedGroups) => {
    setExpanded((prev) => {
      const next = { ...prev, [group]: !prev[group] };
      writeExpanded(next);
      return next;
    });
  };

  const weekUnknown = pending || unavailable;
  const labelStyle = timesheetGridLabelColumn(isServiceJob);
  const showOtherDetails =
    !weekUnknown && expanded.other && model.other.details.length > 0;
  const showWeekDetails =
    !weekUnknown && expanded.week && model.week.payCodes.length > 0;

  const emptyCells = (
    <>
      <Table.Td style={timesheetGridColumns.payCode} />
      {isServiceJob && <Table.Td style={timesheetGridColumns.rate} />}
    </>
  );

  return (
    <Paper withBorder data-testid="timesheet-week-hours-panel">
      <Box p="xs">
        <Title order={4}>Week across all jobs</Title>
      </Box>
      <Table.ScrollContainer minWidth={TIMESHEET_GRID_MIN_WIDTH}>
        <Table style={timesheetGridTableStyle}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={labelStyle} />
              <Table.Th ta="center" style={timesheetGridColumns.payCode}>
                Pay Code
              </Table.Th>
              {isServiceJob && <Table.Th style={timesheetGridColumns.rate} />}
              {TIMESHEET_DAY_KEYS.map((day) => (
                <Table.Th
                  key={day}
                  ta="center"
                  style={timesheetGridColumns.day}
                >
                  {TIMESHEET_DAY_LABELS[day]}
                </Table.Th>
              ))}
              <Table.Th ta="center" style={timesheetGridColumns.total}>
                Total
              </Table.Th>
              <Table.Th style={timesheetGridColumns.actions} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr data-testid="timesheet-week-hours-row-this">
              <Table.Td style={labelStyle} fw={600}>
                This timesheet
              </Table.Td>
              {emptyCells}
              <ValueCells
                days={model.thisTimesheet.days}
                total={model.thisTimesheet.total}
              />
              <Table.Td style={timesheetGridColumns.actions} />
            </Table.Tr>

            <Table.Tr data-testid="timesheet-week-hours-row-other">
              <Table.Td style={labelStyle} fw={600}>
                <Group gap="xs">
                  {`Other jobs (${
                    weekUnknown ? PLACEHOLDER : model.other.timesheetCount
                  })`}
                  {!weekUnknown && model.other.details.length > 0 && (
                    <GroupToggle
                      expanded={expanded.other}
                      label="Toggle other jobs"
                      testId="timesheet-week-hours-toggle-other"
                      onToggle={() => toggle('other')}
                    />
                  )}
                </Group>
              </Table.Td>
              {emptyCells}
              <ValueCells
                days={model.other.days}
                total={model.other.total}
                placeholder={weekUnknown}
              />
              <Table.Td style={timesheetGridColumns.actions} />
            </Table.Tr>

            {showOtherDetails &&
              model.other.details.map((detail, index) => (
                <Table.Tr
                  key={`${detail.jobLabel}-${detail.payCodeName}-${index}`}
                  data-testid={`timesheet-week-hours-detail-other-${index}`}
                >
                  <Table.Td style={labelStyle} pl="lg">
                    {detail.jobLabel}
                  </Table.Td>
                  <Table.Td ta="center" style={timesheetGridColumns.payCode}>
                    {detail.payCodeName}
                  </Table.Td>
                  {isServiceJob && (
                    <Table.Td style={timesheetGridColumns.rate} />
                  )}
                  <ValueCells days={detail.days} total={detail.total} />
                  <Table.Td style={timesheetGridColumns.actions} />
                </Table.Tr>
              ))}

            <Table.Tr data-testid="timesheet-week-hours-row-week">
              <Table.Td style={labelStyle} fw={600}>
                <Group gap="xs">
                  Week
                  {!weekUnknown && model.week.payCodes.length > 0 && (
                    <GroupToggle
                      expanded={expanded.week}
                      label="Toggle week pay codes"
                      testId="timesheet-week-hours-toggle-week"
                      onToggle={() => toggle('week')}
                    />
                  )}
                </Group>
              </Table.Td>
              {emptyCells}
              <ValueCells
                days={model.week.days}
                total={model.week.total}
                placeholder={weekUnknown}
              />
              <Table.Td style={timesheetGridColumns.actions} />
            </Table.Tr>

            {showWeekDetails &&
              model.week.payCodes.map((row) => (
                <Table.Tr
                  key={row.payCodeName}
                  data-testid={`timesheet-week-hours-detail-week-${row.payCodeName}`}
                >
                  <Table.Td style={labelStyle} />
                  <Table.Td ta="center" style={timesheetGridColumns.payCode}>
                    {row.payCodeName}
                  </Table.Td>
                  {isServiceJob && (
                    <Table.Td style={timesheetGridColumns.rate} />
                  )}
                  <ValueCells days={row.days} total={row.total} />
                  <Table.Td style={timesheetGridColumns.actions} />
                </Table.Tr>
              ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {unavailable && (
        <Box p="xs">
          <Text c="dimmed" size="sm">
            Other timesheet hours are unavailable.
          </Text>
        </Box>
      )}
    </Paper>
  );
};
